import { Link, useNavigate } from "react-router-dom";
import {
  TrendingDown,
  ShieldCheck,
  BarChart3,
  Activity,
  ScrollText,
  Workflow,
  FileSearch,
  ClipboardCheck,
  ShieldHalf,
  ArrowRight,
  Check,
  X,
} from "lucide-react";
import FinalCTA from "../components/FinalCTA";

const HERO_IMG =
  "https://images.unsplash.com/photo-1549757521-4160565ff3de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc4Mjg1ODIzM3ww&ixlib=rb-4.1.0&q=85";

const benefits = [
  {
    icon: TrendingDown,
    title: "Cost Savings Through Efficiency",
    copy: "Streamline workflows, reduce process friction, and improve internal coordination.",
  },
  {
    icon: ShieldCheck,
    title: "Regulatory Compliance",
    copy: "Strengthen oversight and reduce the likelihood of avoidable compliance issues and penalties.",
  },
  {
    icon: BarChart3,
    title: "Predictable Performance & ROI",
    copy: "Focus on defined deliverables, repeatable methods, and practical business outcomes.",
  },
  {
    icon: Activity,
    title: "Operational Stability",
    copy: "Build resilient processes that support continuity and long-term growth.",
  },
];

const services = [
  { icon: ScrollText, title: "Governance, Risk & Compliance (GRC) Frameworks" },
  { icon: ShieldHalf, title: "Operational Resilience Planning" },
  { icon: Workflow, title: "Process Optimization" },
  { icon: FileSearch, title: "Compliance Due Diligence" },
  { icon: ClipboardCheck, title: "Internal Controls & Governance Support" },
];

const Home = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-navy overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-28 sm:py-36 lg:py-44">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
            Governance · Risk · Compliance · Resilience
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white max-w-4xl leading-[1.1]">
            Consulting for Governance, Compliance, and Operational Resilience
          </h1>
          <p className="mt-7 text-base sm:text-lg leading-relaxed text-slate-300 max-w-2xl">
            Yana Capital Group helps organizations improve efficiency, strengthen governance, and
            reduce compliance exposure through stable, well-defined consulting services designed for
            predictable outcomes.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-primary-cta"
              onClick={() => navigate("/contact")}
              className="bg-gold text-white hover:bg-gold-hover transition-colors duration-200 px-8 py-4 text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2"
            >
              Request a Consultation <ArrowRight size={17} />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => navigate("/services")}
              className="bg-transparent border border-slate-500 text-white hover:border-white transition-colors duration-200 px-8 py-4 text-sm font-medium tracking-wide"
            >
              Explore Our Services
            </button>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
              Our Approach
            </p>
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy">
              Why Organizations Choose a Disciplined Approach
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base sm:text-lg leading-relaxed text-slate-600">
              Our work focuses on operational efficiency, governance, risk, and compliance frameworks
              that help businesses run better, reduce avoidable disruption, and stay aligned with
              regulatory expectations.
            </p>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600">
              Rather than high-risk transformation bets, our services are built around
              routine execution, structured assessments, and measurable operational improvements.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="grid gap-px bg-slate-200 sm:grid-cols-2 border border-slate-200">
            {benefits.map((b) => (
              <div
                key={b.title}
                data-testid={`benefit-card-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white p-10 sm:p-12 hover:bg-slate-50 transition-colors duration-300"
              >
                <b.icon size={32} strokeWidth={1.4} className="text-gold" />
                <h3 className="mt-6 text-xl font-medium text-navy">{b.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-slate-600">{b.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
                Core Advisory Areas
              </p>
              <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy max-w-xl">
                Stable services for structure, control, and reliability
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-preview-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-gold transition-colors"
            >
              View all services <ArrowRight size={16} />
            </Link>
          </div>

          <div className="divide-y divide-slate-200 border-t border-slate-200">
            {services.map((s, i) => (
              <Link
                to="/services"
                key={s.title}
                className="group flex items-center gap-6 py-7 hover:bg-slate-50 -mx-4 px-4 transition-colors"
              >
                <span className="font-mono text-sm text-gold w-10 shrink-0">0{i + 1}</span>
                <s.icon size={26} strokeWidth={1.4} className="text-navy shrink-0" />
                <span className="text-lg sm:text-xl font-medium text-navy">{s.title}</span>
                <ArrowRight size={18} className="ml-auto text-slate-300 group-hover:text-gold transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stability Comparison */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
              A Disciplined Approach
            </p>
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy">
              A Disciplined, Structured Alternative
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600">
              Yana Capital Group focuses on practical business needs with clear
              operating impact — disciplined improvements that support continuity, accountability, and
              sustainable performance.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-white p-10">
              <h3 className="text-lg font-semibold text-sage flex items-center gap-2">
                <Check size={20} /> Our Approach
              </h3>
              <ul className="mt-6 space-y-4">
                {[
                  "Routine, well-defined business needs",
                  "Predictable deliverables & implementation paths",
                  "Stable, recurring business value",
                  "Strong controls reduce regulatory exposure",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-700">
                    <Check size={18} className="text-sage mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-navy p-10">
              <h3 className="text-lg font-semibold text-slate-300 flex items-center gap-2">
                <X size={20} /> High-Risk Alternatives
              </h3>
              <ul className="mt-6 space-y-4">
                {[
                  "Aggressive, unproven transformation bets",
                  "One-off projects with unclear ownership",
                  "Initiatives without measurable controls",
                  "Outcomes dependent on uncertain timing",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-400">
                    <X size={18} className="text-slate-500 mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
              About Yana Capital Group
            </p>
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy">
              Built Around Confidence, Clarity, and Control
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600">
              We specialize in governance, risk, and compliance frameworks, operational resilience
              planning, and compliance due diligence — all focused on stability, structure, and
              operational reliability. Our
              approach is designed to help clients reduce uncertainty, strengthen internal operations,
              and move forward with confidence.
            </p>
            <Link
              to="/about"
              data-testid="about-preview-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-gold transition-colors"
            >
              Learn more about us <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1517502884422-41eaead166d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBib2FyZHJvb218ZW58MHx8fHwxNzgyODU4MjMzfDA&ixlib=rb-4.1.0&q=85"
              alt="Yana Capital Group boardroom"
              className="w-full h-[420px] object-cover"
            />
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default Home;
